import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { INavigationItem } from '@nrwl/navigation-abstract';
import {
  HelpersService,
  TestListService,
  LmapService,
  TestTypeValue,
  TestListEntity,
  TEST_STATE
} from '@nrwl/common-lib';
import { ITestInfoValue, ITimePickerValue } from '@nrwl/breadcrumb-actions-timepicker';
import { CollectorApiService } from '@nrwl/test-setup';

@Component({
  selector: 'nrwl-final-test-reports',
  templateUrl: './final.test.reports.component.html',
  styleUrls: ['./final.test.reports.component.scss']
})
export class FinalTestReportsComponent implements OnInit, AfterViewInit, OnDestroy {
  reports = [];
  type: string;
  isLoading: boolean;
  runId: number;
  scheduleId: number;
  reportName: string;
  reportStartTime: string;
  reportStatus = '';
  classicReport: boolean;
  expertView: boolean;
  source: string;
  layeredSource = [];
  _testName: string;
  selectedFilter = '';
  breadCrumbValues: Array<INavigationItem> = [];
  testInfoValue: ITestInfoValue = {
    isVisible: true,
    isActionHidden: true
  };
  timePickerValue: ITimePickerValue = {
    isVisible: false,
    isFixed: false
  };
  filteredReports = [];
  failedState = [];
  passedState = [];
  //eslint-disable-next-line
  test: any;
  //eslint-disable-next-line
  cacheObj: any;
  //eslint-disable-next-line
  allTests: any;
  onDemandTest: boolean;
  selectedTimestamp: any;
  isTestRunning = false;
  private readonly _unsubscribe: Subject<void> = new Subject();
  @ViewChild('printSection') printSection!: ElementRef;
  mode: any;
  hideExpertView = false;
  timeoutId: any;
  constructor(
    private activatedRoute: ActivatedRoute,
    private collectorApiService: CollectorApiService,
    private readonly helperService: HelpersService,
    private readonly testListService: TestListService,
    private readonly lmap: LmapService
  ) {
    this.reports = [];
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.type = this.activatedRoute.snapshot.params['type'];
    this.scheduleId = this.activatedRoute.snapshot.params['id'];
    this._handleQueryParams();
    this.testListService
      .getValuesAsync()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(
        testLists => {
          if (testLists) {
            const selecteTest: TestListEntity = testLists.find(
              data => data.getId() === this.scheduleId
            );
            this.mode = selecteTest.getMode();
            this.onDemandTest = selecteTest ? selecteTest.isRunOnceTest() : true;
            this.getQueryReportFinalResults();
          } else {
            this.isLoading = false;
          }
        },
        err => {
          this.isLoading = false;
        }
      );
  }

  ngAfterViewInit(): void {
    this.setFocusToSelectedReport();
  }

  setFocusToSelectedReport(): void {
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      const el = document.querySelector('.active-report');
      if (el) {
        clearTimeout(this.timeoutId);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        this.setFocusToSelectedReport();
      }
    }, 3000);
  }

  getQueryReportFinalResults(): void {
    //on-going test will have single run id, so get the enitre final results and show last final 30 results
    this.collectorApiService
      .getQueryReport(this.scheduleId, 'final')
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(
        res => {
          let index = 0;
          res.data.forEach(resData => {
            const translatedReport = this.lmap.translateLmapReportToAngular(resData);
            const instanceStartTime = this.lmap.getTagValue(
              resData['ietf-lmap-report:report'].result[0].tag,
              'instance-start-time'
            );
            const endTime = resData['ietf-lmap-report:report'].date;
            const tableType = this.lmap.getTagValue(
              resData['ietf-lmap-report:report'].result[0].tag,
              'table-type'
            );
            // Verdict reason will be found from table types
            if (
              tableType === 'system-info' ||
              tableType === 'cpu-usage' ||
              tableType === 'interfaces' ||
              tableType === 'processes'
            ) {
              return;
            }

            if (translatedReport) {
              if (this.isTestRunning && this.runId === translatedReport?.[0]?.['runId']) {
                return;
              }
              const reportData = this.reports.find(data => data.startTime === instanceStartTime);
              if (!reportData) {
                this.reports.push({
                  name: `Report ${index + 1} - ${this.helperService.getReportDateTimeString(
                    instanceStartTime
                  )}`,
                  value: `report ${index + 1}`,
                  runId: translatedReport?.[0]?.['runId'],
                  startTime: instanceStartTime,
                  endTime: endTime,
                  data: [resData],
                  reportStatus:
                    this.type === TestTypeValue.TRUESPEED
                      ? this.getTruespeedReportVerdictStatus([resData])
                      : this.type === TestTypeValue.HTTP || this.type === TestTypeValue.FTP
                      ? this.getHttpReportVerdictStatus([resData])
                      : this.getVerdictStatus([resData])
                });
                index++;
              } else {
                reportData.data.push(resData);
                if (this.type === TestTypeValue.TRUESPEED && this.mode !== 'client-to-ma') {
                  if (reportData.reportStatus !== 'fail') {
                    reportData.reportStatus =
                      this.type === TestTypeValue.TRUESPEED
                        ? this.getTruespeedReportVerdictStatus(reportData.data)
                        : this.type === TestTypeValue.HTTP || this.type === TestTypeValue.FTP
                        ? this.getHttpReportVerdictStatus(reportData.data)
                        : this.getVerdictStatus(reportData.data);
                  }
                } else {
                  reportData.reportStatus =
                    this.type === TestTypeValue.TRUESPEED
                      ? this.getTruespeedReportVerdictStatus(reportData.data)
                      : this.type === TestTypeValue.HTTP || this.type === TestTypeValue.FTP
                      ? this.getHttpReportVerdictStatus(reportData.data)
                      : this.getVerdictStatus(reportData.data);
                }
              }
            }
          });
          if (this.reports.length) {
            this.reports.sort((a, b) => {
              return Number(new Date(b.startTime)) - Number(new Date(a.startTime));
            });

            let reportIndex = 0;
            if (typeof this.selectedTimestamp === 'number' && !isNaN(this.selectedTimestamp)) {
              reportIndex = this.getReportIndexForTimestamp();
            }
            this.runId = this.reports[reportIndex].runId;
            this.filteredReports = this.reports;
            this.reportName = this.reports[reportIndex].name;
            this.reportStartTime = this.reports[reportIndex].startTime;
            this.failedState = this.reports.filter(item => item.reportStatus === 'fail');
            this.passedState = this.reports.filter(item => item.reportStatus === 'pass');
            this.reportStatus = this.reports[reportIndex].reportStatus;
            this.isLoading = false;
          } else {
            this.reports = [];
            this.isLoading = false;
          }
        },
        err => {
          this.reports = [];
          this.isLoading = false;
        }
      );
  }

  //eslint-disable-next-line
  getVerdictStatus(data: any): string {
    for (let i = 0; i < data.length; i++) {
      //  const results = data[i]['ietf-lmap-report:report'].result;
      const results = this.lmap.translateLmapReportToAngular(data[i]);
      for (let j = 0; j < results?.length; j++) {
        const verdictItem = results[j]['verdict']?.toLowerCase();
        if (verdictItem === 'fail') {
          return 'fail';
        } else if (verdictItem === 'inconclusive') {
          return 'warn';
        } else if (verdictItem !== 'pass') {
          return 'none';
        }
      }
    }

    return 'pass';
  }

  //eslint-disable-next-line
  getTruespeedReportVerdictStatus(data): string {
    if (data && data.length) {
      const finalReportsData = data.filter(report =>
        report['ietf-lmap-report:report'].result[0]?.tag.includes('result-type:final')
      );
      if (finalReportsData.length > 0) {
        const tables =
          finalReportsData[finalReportsData.length - 1]['ietf-lmap-report:report'].result[0]
            .table[0];
        if (tables?.row?.length) {
          const downstreamVerdictIndex = tables.column.indexOf('engine-a-to-b-diagnosis');
          const upstreamVerdictIndex = tables.column.indexOf('engine-b-to-a-diagnosis');
          const originator = tables.column.indexOf('originator');
          const values = tables.row;
          let warnFlag = false;
          if (tables.column.indexOf('truespeed-type') === -1) {
            // Advanced Test
            this.hideExpertView = false;
            if (upstreamVerdictIndex !== -1 || downstreamVerdictIndex !== -1) {
              for (let m = 0; m < values.length; m++) {
                if (
                  values[m].value[upstreamVerdictIndex] &&
                  values[m].value[upstreamVerdictIndex] !== ''
                ) {
                  if (
                    values[m].value[upstreamVerdictIndex].indexOf('ALL_OK') === -1 &&
                    values[m].value[upstreamVerdictIndex].indexOf('TCP_EFFICIENCY_LOW') === -1
                  ) {
                    return 'fail';
                  } else if (
                    values[m].value[upstreamVerdictIndex].trim() !== 'ALL_OK' &&
                    values[m].value[upstreamVerdictIndex].trim() !== 'TCP_EFFICIENCY_LOW'
                  ) {
                    // contains other text in addition to ALL_OK
                    warnFlag = true;
                  }
                }
                if (
                  values[m].value[downstreamVerdictIndex] &&
                  values[m].value[downstreamVerdictIndex] !== ''
                ) {
                  if (
                    values[m].value[downstreamVerdictIndex].indexOf('ALL_OK') === -1 &&
                    values[m].value[downstreamVerdictIndex].indexOf('TCP_EFFICIENCY_LOW') === -1
                  ) {
                    return 'fail';
                  } else if (
                    values[m].value[downstreamVerdictIndex].trim() !== 'ALL_OK' &&
                    values[m].value[downstreamVerdictIndex].trim() !== 'TCP_EFFICIENCY_LOW'
                  ) {
                    // contains other text in addition to ALL_OK
                    warnFlag = true;
                  }
                }
              }
            } else {
              return 'fail';
            }
          } else {
            // Thin client Test
            this.hideExpertView = true;
            const actualThroughputIndex = tables.column.indexOf('avg-thruput');
            const targetThroughputIndex = tables.column.indexOf('ideal-thruput');
            let testStatus = 'fail';
            let upstreamPass = false;
            let downStreamPass = false;
            if (actualThroughputIndex !== -1 && targetThroughputIndex !== -1) {
              for (let n = 1; n < values.length; n++) {
                const actualValue: any = parseFloat(values[n].value[actualThroughputIndex]).toFixed(
                  1
                );
                const targetValue: any = parseFloat(values[n].value[targetThroughputIndex]).toFixed(
                  1
                );
                if (values[n].value[originator] === 'engine-b') {
                  upstreamPass = (actualValue / targetValue) * 100 >= 90 ? true : false;
                } else if (values[n].value[originator] === 'engine-a') {
                  downStreamPass = (actualValue / targetValue) * 100 >= 90 ? true : false;
                }
                if (upstreamPass && downStreamPass) {
                  testStatus = 'pass';
                } else {
                  testStatus = 'fail';
                }
              }
            } else {
              return 'fail';
            }
            return testStatus;
          }
        } else {
          return 'inconclusive';
        }
      } else {
        return 'inconclusive';
      }
      return 'pass';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getHttpReportVerdictStatus(data): string {
    if (data && data.length) {
      const finalReportsData = data.filter(report =>
        report['ietf-lmap-report:report'].result[0]?.tag.includes('result-type:final')
      );
      if (finalReportsData.length > 0) {
        const tables =
          finalReportsData[finalReportsData.length - 1]['ietf-lmap-report:report'].result[0]
            .table[0];
        if (tables?.row?.length) {
          const downstreamVerdictIndex = tables.column.indexOf('engine-b-to-a-diagnosis');
          const upstreamVerdictIndex = tables.column.indexOf('engine-a-to-b-diagnosis');
          const originatorIndex = tables.column.indexOf('originator');
          const values = tables.row;
          let warnFlag = false;
          if (
            (upstreamVerdictIndex !== -1 && originatorIndex !== -1) ||
            (downstreamVerdictIndex !== -1 && originatorIndex !== -1)
          ) {
            for (let m = 0; m < values.length; m++) {
              if (
                values[m].value[upstreamVerdictIndex] &&
                values[m].value[upstreamVerdictIndex] !== '' &&
                values[m].value[originatorIndex] === 'engine-a'
              ) {
                if (
                  values[m].value[upstreamVerdictIndex].indexOf('ALL_OK') === -1 &&
                  values[m].value[upstreamVerdictIndex].indexOf('TCP_EFFICIENCY_LOW') === -1
                ) {
                  return 'fail';
                } else if (
                  values[m].value[upstreamVerdictIndex].trim() !== 'ALL_OK' &&
                  values[m].value[upstreamVerdictIndex].trim() !== 'TCP_EFFICIENCY_LOW'
                ) {
                  // contains other text in addition to ALL_OK
                  warnFlag = true;
                }
              }
              if (
                values[m].value[downstreamVerdictIndex] &&
                values[m].value[downstreamVerdictIndex] !== '' &&
                values[m].value[originatorIndex] === 'engine-b'
              ) {
                if (
                  values[m].value[downstreamVerdictIndex].indexOf('ALL_OK') === -1 &&
                  values[m].value[downstreamVerdictIndex].indexOf('TCP_EFFICIENCY_LOW') === -1
                ) {
                  return 'fail';
                } else if (
                  values[m].value[downstreamVerdictIndex].trim() !== 'ALL_OK' &&
                  values[m].value[downstreamVerdictIndex].trim() !== 'TCP_EFFICIENCY_LOW'
                ) {
                  // contains other text in addition to ALL_OK
                  warnFlag = true;
                }
              }
            }
          } else {
            return 'fail';
          }
        } else {
          return 'inconclusive';
        }
      } else {
        return 'inconclusive';
      }
      return 'pass';
    }
  }

  //eslint-disable-next-line
  openReport(report: any): void {
    this.reportName = report.name;
    this.reportStartTime = report.startTime;
    this.runId = report.runId;
    this.reportStatus = this.reportStatus ? this.reportStatus : report.reportStatus;

    this.hideExpertView = false;

    if (report.data && this.type === TestTypeValue.TRUESPEED) {
      for (const reportItem of report.data) {
        const result = reportItem['ietf-lmap-report:report'].result;
        if (result) {
          for (const res of result) {
            if (res.table) {
              for (const table of res.table) {
                const columnIndex = table.column.indexOf('truespeed-type');
                if (columnIndex !== -1 && table.row.length > 0) {
                  const firstRowValue = table.row[0].value[columnIndex];
                  if (firstRowValue === 'thin-engine') {
                    this.hideExpertView = true;
                    return;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  print() {
    window.print();
  }

  private _handleQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this._unsubscribe)).subscribe(queryParams => {
      this.source = queryParams.source;
      this._testName = queryParams.testName;
      this.layeredSource = queryParams.layeredSource;
      this.selectedTimestamp = queryParams?.selectedTimestamp
        ? Number(queryParams.selectedTimestamp)
        : null;
      this.runId = queryParams.runId ?? null;
      this.isTestRunning = queryParams.testStatus === TEST_STATE.RUNNING;
    });
    this._handleBreadcrumbValues();
  }

  private _handleBreadcrumbValues(): void {
    const breadCrumbValues: Array<INavigationItem> = [];
    if (this.layeredSource?.length) {
      this.layeredSource.forEach(breadCrumbLabel => {
        breadCrumbValues.push({ label: breadCrumbLabel });
      });
    } else if (this.source) {
      breadCrumbValues.push({ label: this.source });
    }
    const labelValue = this._testName ? `${this._testName}` : this.type.toLocaleUpperCase();
    breadCrumbValues.push({ label: labelValue });

    this.breadCrumbValues = breadCrumbValues;
  }

  filterType(val: string): void {
    if (!val) {
      this.filteredReports = this.reports;
    } else {
      this.filteredReports = this.reports.filter(item => item.reportStatus === val);
    }
    this.openReport(this.filteredReports[0]);
  }

  getReportIndexForTimestamp(): number {
    const filteredIndexes = this.reports.reduce((IndexArr: number[], item, index) => {
      //eslint-disable-next-line
      if (this.isTimestampWithinRange(item.startTime, item.endTime, this.selectedTimestamp!)) {
        IndexArr.push(index);
      }
      return IndexArr;
    }, []);

    // Add nearest index as fallback if No match found, find nearest startTime
    if (filteredIndexes.length === 0) {
      let nearestIndex = 0;
      let minDiff = Infinity;

      this.reports.forEach((item, index) => {
        const startTimestamp = new Date(item.startTime);
        startTimestamp.setSeconds(0, 0);
        //eslint-disable-next-line
        const diff = Math.abs(startTimestamp.getTime() - this.selectedTimestamp!);
        if (diff < minDiff) {
          minDiff = diff;
          nearestIndex = index;
        }
      });

      filteredIndexes.push(nearestIndex);
    }
    return filteredIndexes.length ? filteredIndexes[0] : 0;
  }

  private isTimestampWithinRange(
    startTime: string | null,
    endTime: string | null,
    timestamp: number
  ): boolean {
    if (!startTime || !endTime) return false;
    const start = new Date(startTime);
    start.setSeconds(0, 0);
    const end = new Date(endTime);
    end.setSeconds(0, 0);
    const startTimestamp = start.getTime();
    const endTimestamp = end.getTime();
    // console.log('startTime', startTimestamp, new Date(start).toLocaleString());
    // console.log('EndTime', endTimestamp, new Date(end).toLocaleString());
    // console.log('TimeStamp', timestamp, new Date(timestamp).toLocaleString());
    return (
      !isNaN(startTimestamp) &&
      !isNaN(endTimestamp) &&
      startTimestamp <= timestamp &&
      endTimestamp >= timestamp
    );
  }

  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this._unsubscribe.next();
    this._unsubscribe.complete();
    clearTimeout(this.timeoutId);
  }
}
